// Tells the API about the errors only the user's browser saw, so the team can
// read in the server logs which request failed, after how long, and whether
// the browser thought it was online. Without it a timeout on a client's
// machine and a slow server look the same.

const MAX_PENDING = 10;
const JWT_PATTERN = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const NETWORK_KINDS = ['NETWORK_TIMEOUT', 'NETWORK_ERROR'];

let pending = [];

/**
 * Keep what identifies a request and drop what could identify a person: the
 * tokens in file URLs, a search keyword, the fragment and the values of the
 * query string (an email, a name).
 * @param {string} url - The URL of the failed request.
 * @returns {string|undefined} The path, with the query parameter names only.
 */
export const describeUrl = (url) => {
  if (typeof url !== 'string') return undefined;
  const [beforeFragment] = url.replace(JWT_PATTERN, '<token>').split('#');
  const [rawPath, query] = beforeFragment.split('?');
  const path = rawPath.replace(/\/search\/[^/]*/g, '/search/<keyword>');
  if (!query) return path;
  const names = query
    .split('&')
    .map((pair) => pair.split('=')[0])
    .filter(Boolean);
  return `${path}?${names.join('&')}`;
};

const currentUserId = () => {
  try {
    // eslint-disable-next-line no-underscore-dangle
    return JSON.parse(globalThis.localStorage.getItem('user'))?._id;
  } catch (e) {
    return undefined;
  }
};

/**
 * Describe an error the way the API logs it.
 * @param {Error} error - What dispatchAPI rejected with, or a page bug.
 * @param {string} kind - NETWORK_TIMEOUT, NETWORK_ERROR or UNEXPECTED.
 * @returns {Object} The report.
 */
export const buildClientErrorReport = (error, kind) => ({
  kind,
  method: error?.config?.method?.toUpperCase(),
  url: describeUrl(error?.config?.url),
  elapsedMs: error?.elapsedMs,
  online: globalThis.navigator?.onLine,
  visibility: globalThis.document?.visibilityState,
  page: globalThis.location?.pathname,
  user: currentUserId(),
  message: error?.message
});

const send = (reports) => {
  try {
    return Boolean(
      globalThis.navigator?.sendBeacon?.(
        `${process.env.REACT_APP_API_URL}/client-errors`,
        // A string goes out as text/plain: no preflight on the API origin.
        JSON.stringify({ reports })
      )
    );
  } catch (e) {
    return false;
  }
};

/**
 * Send the reports kept while the network was failing. Called after a request
 * succeeds, when the network is back for sure.
 * @returns {void}
 */
export const flushClientErrors = () => {
  if (!pending.length) return;
  if (send(pending)) pending = [];
};

/**
 * Report an error. A page bug goes out at once; a request that got no answer
 * waits for the next successful one, a beacon sent offline would be lost.
 * @param {Error} error - What dispatchAPI rejected with, or a page bug.
 * @param {string} kind - NETWORK_TIMEOUT, NETWORK_ERROR or UNEXPECTED.
 * @returns {void}
 */
export const reportClientError = (error, kind) => {
  pending = [...pending, buildClientErrorReport(error, kind)].slice(
    -MAX_PENDING
  );
  if (!NETWORK_KINDS.includes(kind)) flushClientErrors();
};

// For the tests only.
export const pendingClientErrors = () => pending;
export const clearClientErrors = () => {
  pending = [];
};
